import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Hash, TrendingUp, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Card from '@/components/ui/Card';

// --- Types ---

interface SearchTerm {
  query: string;
  search_count: number;
  avg_results: number;
}

interface SearchVolumeEntry {
  period: string;
  search_count: string;
  unique_queries: string;
}

interface SearchAnalyticsData {
  popular_terms: SearchTerm[];
  zero_result_terms: SearchTerm[];
  total_searches: number;
  unique_queries: number;
  avg_results_per_search: string | null;
  zero_result_count: number;
  search_volume: SearchVolumeEntry[];
}

// --- Date range options ---

type DateRange = '7' | '30' | '90' | 'all';

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

// --- Hook ---

function useSearchAnalytics(range: DateRange) {
  return useQuery<SearchAnalyticsData>({
    queryKey: ['admin-search-analytics', range],
    queryFn: () => api.get<SearchAnalyticsData>(`/admin/search-analytics?days=${range}`),
  });
}

// --- Empty state ---

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Search className="h-12 w-12 text-stone-300 mb-4" />
      <h2 className="text-lg font-semibold text-stone-700 mb-2">No search data yet</h2>
      <p className="text-stone-500 max-w-sm">
        Once customers start searching, your analytics will appear here.
      </p>
    </div>
  );
}

// --- Tooltip state ---

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  content: string;
}

// --- Main component ---

export default function SearchAnalytics() {
  const [range, setRange] = useState<DateRange>('30');
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, content: '' });

  const { data, isLoading, isError } = useSearchAnalytics(range);

  if (isLoading) return <LoadingSpinner className="py-16" />;
  if (isError) return <p className="text-center py-16 text-stone-500">Failed to load search analytics.</p>;

  const {
    total_searches,
    unique_queries,
    avg_results_per_search,
    zero_result_count,
    popular_terms,
    zero_result_terms,
    search_volume,
  } = data!;

  const zeroResultRate =
    total_searches > 0 ? ((zero_result_count / total_searches) * 100).toFixed(1) : '0.0';

  const statCards = [
    { label: 'Total Searches', value: total_searches.toLocaleString(), icon: Search },
    { label: 'Unique Queries', value: unique_queries.toLocaleString(), icon: Hash },
    {
      label: 'Avg Results',
      value: avg_results_per_search ? parseFloat(avg_results_per_search).toFixed(1) : '—',
      icon: TrendingUp,
    },
    { label: 'Zero Result Rate', value: `${zeroResultRate}%`, icon: AlertCircle },
  ];

  const trendLabel = DATE_RANGE_OPTIONS.find((o) => o.value === range)?.label ?? 'Search Volume';

  const handleBarMouseMove = (e: React.MouseEvent<HTMLDivElement>, content: string) => {
    const rect = e.currentTarget.closest('.trend-chart-root')?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({
      visible: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 40,
      content,
    });
  };

  const hideTooltip = () => setTooltip((t) => ({ ...t, visible: false }));

  // Compute max search count for bar heights
  const maxCount = Math.max(...search_volume.map((v) => parseInt(v.search_count)), 1);

  // Format period label based on range
  const formatPeriod = (period: string) => {
    const d = new Date(period);
    if (range === 'all') {
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    if (range === '90') {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Search className="h-7 w-7 text-stone-400" />
        <div>
          <h1 className="text-2xl font-display font-bold text-stone-900">Search Analytics</h1>
          <p className="text-sm text-stone-500">Understand what your customers are searching for.</p>
        </div>

        <div className="ml-auto">
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as DateRange)}
            className="text-sm border border-stone-200 rounded-md px-3 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300"
          >
            {DATE_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {total_searches === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {statCards.map((stat) => (
              <Card key={stat.label} className="p-4 text-center">
                <stat.icon className="h-5 w-5 mx-auto text-stone-400 mb-2" aria-hidden="true" />
                <p className="text-2xl font-bold text-stone-900">{stat.value}</p>
                <p className="text-xs text-stone-500 mt-1">{stat.label}</p>
              </Card>
            ))}
          </div>

          {/* Search Volume Trend */}
          {search_volume.length > 0 && (
            <Card className="p-6 mb-8">
              <h2 className="font-semibold text-stone-900 mb-4">Search Volume — {trendLabel}</h2>
              <div className="trend-chart-root relative" onMouseLeave={hideTooltip}>
                {tooltip.visible && (
                  <div
                    className="pointer-events-none absolute z-10 bg-stone-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg"
                    style={{ left: tooltip.x, top: tooltip.y }}
                  >
                    {tooltip.content}
                  </div>
                )}

                <div className="flex gap-1 h-40 items-end">
                  {search_volume.map((v, i) => {
                    const count = parseInt(v.search_count);
                    const uniqueQ = parseInt(v.unique_queries);
                    const heightPct = (count / maxCount) * 100;
                    const label = formatPeriod(v.period);
                    const tooltipContent = `${label}: ${count} search${count !== 1 ? 'es' : ''} (${uniqueQ} unique)`;

                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                        <span className="text-xs text-stone-500 leading-none">{count}</span>
                        <div className="w-full flex-1 flex items-end">
                          <div
                            className="w-full rounded-t bg-emerald-500 hover:bg-emerald-400 transition-colors duration-150 cursor-default"
                            style={{ height: `${heightPct}%` }}
                            onMouseMove={(e) => handleBarMouseMove(e, tooltipContent)}
                          />
                        </div>
                        <span className="text-xs text-stone-400 truncate w-full text-center leading-none">
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Popular Terms */}
            <Card className="p-6">
              <h2 className="font-semibold text-stone-900 mb-4">Popular Search Terms</h2>
              {popular_terms.length === 0 ? (
                <div className="py-8 text-center">
                  <TrendingUp className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                  <p className="text-sm text-stone-400">No search data yet</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {popular_terms.map((term, i) => (
                    <div key={term.query} className="flex items-center gap-3 py-2">
                      <span className="text-sm font-semibold text-stone-400 w-6">{i + 1}</span>
                      <a
                        href={`/search?q=${encodeURIComponent(term.query)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 text-sm font-medium text-stone-900 truncate hover:text-emerald-700 hover:underline"
                      >
                        {term.query}
                      </a>
                      <div className="text-right shrink-0">
                        <span className="text-sm font-semibold text-stone-900">{term.search_count}</span>
                        <span className="text-xs text-stone-500 ml-1">searches</span>
                      </div>
                      <span className="text-xs text-stone-400 w-16 text-right shrink-0">
                        ~{Math.round(term.avg_results)} results
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Zero Result Terms */}
            <Card className="p-6">
              <h2 className="font-semibold text-stone-900 mb-4">Zero Result Searches</h2>
              {zero_result_terms.length === 0 ? (
                <div className="py-8 text-center">
                  <AlertCircle className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                  <p className="text-sm text-stone-400">No zero-result searches</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {zero_result_terms.map((term, i) => (
                    <div key={term.query} className="flex items-center gap-3 py-2">
                      <span className="text-sm font-semibold text-red-400 w-6">{i + 1}</span>
                      <a
                        href={`/search?q=${encodeURIComponent(term.query)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 text-sm font-medium text-stone-900 truncate hover:text-emerald-700 hover:underline"
                      >
                        {term.query}
                      </a>
                      <div className="text-right shrink-0">
                        <span className="text-sm font-semibold text-red-600">{term.search_count}</span>
                        <span className="text-xs text-stone-500 ml-1">times</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
